import { useState } from "react";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { SITE, SERVICE_TYPES } from "../constants/site";
import { Reveal, SectionHead } from "./Reveal";

const INIT = { name: "", email: "", phone: "", company: "", service_type: SERVICE_TYPES[0], message: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(INIT);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/quotes", form);
      toast.success("Demande envoyée. Nous vous répondons sous 24h.");
      setForm(INIT);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSending(false); }
  };

  const INFO = [
    { icon: Phone, v: SITE.phone, href: `tel:${SITE.phone.replace(/\s/g, "")}` },
    { icon: Phone, v: SITE.phone2, href: `tel:${SITE.phone2.replace(/\s/g, "")}` },
    { icon: Mail, v: SITE.email, href: `mailto:${SITE.email}` },
    { icon: MapPin, v: SITE.address },
    { icon: Clock, v: SITE.hours },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-16">
        <div className="lg:col-span-5">
          <SectionHead eyebrow="05 — Contact & Devis" title="Parlons de votre projet." text="Un logiciel de gestion à concevoir, une commande en gros, un réseau à installer : décrivez votre besoin, nous revenons vers vous avec un devis clair." />
          <Reveal delay={0.1} className="mt-10 space-y-4">
            {INFO.map(({ icon: Icon, v, href }) => (
              <div key={v} className="flex items-center gap-4 text-sm text-navy">
                <span className="grid h-10 w-10 place-items-center bg-ice text-royal"><Icon className="h-4 w-4" /></span>
                {href ? <a href={href} className="underline-link">{v}</a> : <span>{v}</span>}
              </div>
            ))}
            <a data-testid="contact-whatsapp-link" href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="btn-wa mt-4">Écrire sur WhatsApp</a>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="quote-form" className="grid gap-6 border border-navy/10 bg-ice p-8 sm:grid-cols-2 lg:p-10">
            <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} placeholder="Nom complet *" className="field" />
            <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className="field" />
            <input data-testid="quote-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Téléphone *" className="field" />
            <input data-testid="quote-company-input" value={form.company} onChange={set("company")} placeholder="Entreprise / Organisation" className="field" />
            <select data-testid="quote-service-select" value={form.service_type} onChange={set("service_type")} className="field sm:col-span-2">
              {SERVICE_TYPES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <textarea data-testid="quote-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder="Décrivez votre besoin *" className="field resize-none sm:col-span-2" />
            <button data-testid="quote-submit-button" disabled={sending} className="btn-primary justify-center sm:col-span-2 disabled:opacity-60">
              {sending ? "Envoi…" : <>Envoyer la demande <Send className="h-4 w-4" /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
